// src/pages/Lesson.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Lesson, UserProgress, User, DailyCheckIn } from "@/api/entities";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, Play, CheckCircle, Clock, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import ExercisePlayer from "@/components/lessons/ExercisePlayer";
import LessonFeedbackModal from "@/components/lessons/LessonFeedbackModal";
import { saveToR2, saveLessonEvent } from "@/api/r2"; // ⬅️ già importato
// ⬇️ esercizio speciale “obiettivi & ricompense” (lezione 6)
import ExerciseGoals from "@/components/exercises/ExerciseGoals";

export default function LessonPage() {
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showExercise, setShowExercise] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Tracking avanzato riproduzione
  const [totalListenTime, setTotalListenTime] = useState(0);
  const [playbackEvents, setPlaybackEvents] = useState([]);
  const [hasRevisited, setHasRevisited] = useState(false);
  const [lastTick, setLastTick] = useState(null);

  // sorgente audio risolta
  const [resolvedSrc, setResolvedSrc] = useState("");
  const [fallbackTried, setFallbackTried] = useState(false);

  const audioRef = useRef(null);

  // tracking sessione lezione per log R2
  const sessionIdRef = useRef(crypto.randomUUID());
  const heartbeatRef = useRef(null);
  const startedRef = useRef(false);
  const isPlayingRef = useRef(false);

  const urlParams = new URLSearchParams(window.location.search);
  const lessonId = urlParams.get("id");

  const loadLessonData = useCallback(async () => {
    if (!lessonId) {
      navigate(createPageUrl("Lessons"));
      return;
    }

    setIsLoading(true);
    try {
      const currentUser = await User.me();
      const foundLesson = await Lesson.get(lessonId);
      if (!foundLesson) {
        navigate(createPageUrl("Lessons"));
        return;
      }

      const userProgressData = await UserProgress.filter({
        created_by: currentUser.email,
        lesson_id: lessonId,
      });

      setLesson(foundLesson);
      const progressData = userProgressData[0] || null;
      setProgress(progressData);
      if (progressData?.lesson_completed) setHasRevisited(true);
    } catch (error) {
      console.error("Errore caricamento lezione:", error);
      navigate(createPageUrl("Lessons"));
    } finally {
      setIsLoading(false);
    }
  }, [lessonId, navigate]);

  useEffect(() => {
    loadLessonData();
  }, [loadLessonData]);

  // ricarica progress quando l’esercizio 6 salva (emette "rb:checkin-saved")
  useEffect(() => {
    const onSaved = () => loadLessonData();
    window.addEventListener("rb:checkin-saved", onSaved);
    return () => window.removeEventListener("rb:checkin-saved", onSaved);
  }, [loadLessonData]);

  // --- apertura esercizio, sicura contro doppio click
  const openExerciseSafe = useCallback((e) => {
    try { e?.preventDefault?.(); e?.stopPropagation?.(); } catch {}
    Promise.resolve().then(() => requestAnimationFrame(() => setShowExercise(true)));
  }, []);

  // ==== RISOLUZIONE SRC AUDIO: prova R2 → altrimenti proxy ====
  useEffect(() => {
    let cancelled = false;
    setFallbackTried(false);

    (async () => {
      const raw = (lesson?.audio_url || "").trim();
      if (!raw) {
        if (!cancelled) setResolvedSrc("");
        return;
      }

      // Normalizza l'URL "upstream"
      let upstreamStr = raw;
      try {
        const u = new URL(raw, window.location.origin);
        upstreamStr = u.toString();
      } catch {
        // se non è URL assoluto, lascio "raw"
      }

      // Costruisci key R2 dal basename (decodificato)
      let k = upstreamStr.split("/").pop() || upstreamStr;
      try { k = decodeURIComponent(k); } catch {}

      const r2Url = `/api/r2-audio?k=${encodeURIComponent(k)}`;

      try {
        const head = await fetch(r2Url, { method: "HEAD" });
        if (!cancelled && head.ok) {
          setResolvedSrc(r2Url);
          console.log("[Lesson] uso R2:", r2Url);
          return;
        }
        console.warn("[Lesson] R2 non disponibile → fallback proxy");
      } catch (e) {
        console.warn("[Lesson] HEAD R2 errore:", e);
      }

      if (cancelled) return;
      // Fallback proxy (passando l'URL pulito, senza doppio encode)
      const proxy = new URL("/api/media-proxy", window.location.origin);
      proxy.searchParams.set("u", upstreamStr);
      setResolvedSrc(proxy.toString());
      console.log("[Lesson] uso proxy:", proxy.toString());
    })();

    return () => { cancelled = true; };
  }, [lesson?.audio_url]);

  // ---- Helpers tracking → R2 ----
  const getAudioMetrics = () => {
    const a = audioRef.current;
    if (!a) return {};
    return {
      audio_position: Math.round(a.currentTime || 0),
      audio_duration: Math.round(a.duration || 0),
      audio_paused: !!a.paused,
      audio_ended: !!a.ended,
      playback_rate: a.playbackRate || 1,
    };
  };

  const sendEvent = async (type, extra = {}) => {
    try {
      await saveLessonEvent({
        lesson,
        event: {
          type,
          session_id: sessionIdRef.current,
          active_seconds: Math.round(totalListenTime),
          ...getAudioMetrics(),
          ...extra,
        },
      });
    } catch (e) {
      console.warn("[R2] saveLessonEvent queued/offline", e);
    }
  };

  const startHeartbeat = () => {
    if (heartbeatRef.current) return;
    heartbeatRef.current = setInterval(() => {
      const a = audioRef.current;
      if (a && !a.paused && lastTick) {
        const now = Date.now();
        const deltaSec = (now - lastTick) / 1000;
        setTotalListenTime(prev => prev + deltaSec);
        setLastTick(now);
      }
      sendEvent("heartbeat");
    }, 15000);
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  // Visibilità tab
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        sendEvent("visibility_hidden");
      } else {
        if (isPlayingRef.current) setLastTick(Date.now());
        sendEvent("visibility_visible");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id]);

  // ---- Eventi audio ----
  const trackAudioEvent = (type, currentTime = 0, sessionDelta = 0) => {
    setPlaybackEvents(prev => [
      ...prev,
      {
        type,
        timestamp: new Date().toISOString(),
        audio_position: currentTime,
        session_delta: sessionDelta,
      },
    ]);
  };

  const handleAudioPlay = async (e) => {
    isPlayingRef.current = true;
    setLastTick(Date.now());
    trackAudioEvent("play", e?.target?.currentTime || 0, 0);

    if (!startedRef.current) {
      startedRef.current = true;
      sendEvent("start");
    } else {
      sendEvent("resume");
    }

    startHeartbeat();

    if (!progress || !progress.lesson_completed) {
      try {
        const patch = {
          lesson_id: lessonId,
          lesson_completed: true,
          exercise_completed: progress?.exercise_completed || false,
        };
        let updated;
        if (progress?.id) updated = await UserProgress.update(progress.id, patch);
        else updated = await UserProgress.create(patch);
        setProgress(updated);
      } catch (err) {
        console.error("Errore aggiornamento progressi:", err);
      }
    }
  };

  const handleAudioPause = (e) => {
    isPlayingRef.current = false;
    setLastTick(null);
    trackAudioEvent("pause", e.target.currentTime, 0);
    sendEvent("pause");
    stopHeartbeat();
  };

  const handleAudioSeeked = (e) => {
    trackAudioEvent("seek", e.target.currentTime, 0);
    sendEvent("seek", { audio_position: Math.round(e.target.currentTime || 0) });
  };

  const handleAudioTimeUpdate = (e) => {
    if (lastTick && !e.target.paused) {
      const now = Date.now();
      const deltaSec = (now - lastTick) / 1000;
      setTotalListenTime(prev => prev + deltaSec);
      setLastTick(now);
    }
  };

  const handleAudioEnded = async (e) => {
    isPlayingRef.current = false;
    setLastTick(null);
    trackAudioEvent("ended", e.target.duration, 0);
    stopHeartbeat();
    await sendEvent("ended");

    try {
      const audioDuration = Number(e.target.duration || duration || 0);
      const completionPct =
        audioDuration > 0
          ? Math.min(100, Math.round((totalListenTime / audioDuration) * 100))
          : null;

      await saveToR2("lesson_stats", {
        lesson_id: lessonId,
        data: {
          lesson_id: lessonId,
          lesson_title: lesson?.title,
          completed_at: new Date().toISOString(),
          total_listen_time_seconds: Math.round(totalListenTime),
          audio_duration_seconds: Math.round(audioDuration),
          playback_events: playbackEvents,
          was_revisit: hasRevisited,
          completion_percentage: completionPct,
          plays_count: playbackEvents.filter((e) => e.type === "play").length,
        },
        meta: { source: "lesson_player" },
      });

      await sendEvent("complete");
    } catch (err) {
      console.warn("[R2] lesson_stats/complete queued/offline", err);
    }
  };

  const handleAudioError = (e) => {
    console.error("Errore caricamento audio:", e.target.error);
    const map = {
      1: "MEDIA_ERR_ABORTED",
      2: "MEDIA_ERR_NETWORK",
      3: "MEDIA_ERR_DECODE",
      4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
    };
    if (e.target?.error?.code) {
      console.error("Dettagli errore audio:", map[e.target.error.code] || "Sconosciuto");
    }
    console.error("URL originale (lesson.audio_url):", lesson?.audio_url);
    console.error("SRC risolta (resolvedSrc):", resolvedSrc);

    // Fallback di emergenza: se stavo usando R2 e non ho ancora provato il proxy, prova ora
    if (!fallbackTried && resolvedSrc.startsWith("/api/r2-audio")) {
      try {
        const raw = (lesson?.audio_url || "").trim();
        const upstream = new URL("/api/media-proxy", window.location.origin);
        upstream.searchParams.set("u", raw);
        setResolvedSrc(upstream.toString());
        setFallbackTried(true);
        console.warn("[Lesson] errore con R2 → passo a proxy:", upstream.toString());
      } catch {}
    }
  };

  const handleAudioLoadStart = () => {
    if (lesson?.audio_url) console.log("Inizio caricamento audio:", lesson.audio_url);
  };
  const handleAudioLoadedData = () => console.log("Audio caricato con successo!");
  const handleAudioCanPlay = () => console.log("Audio pronto per la riproduzione");

  // cleanup: all'uscita dalla pagina inviamo "complete" e aggiorniamo UserProgress
  useEffect(() => {
    return () => {
      stopHeartbeat();
      if (startedRef.current) {
        sendEvent("complete");
      }
      (async () => {
        try {
          const created_by = progress?.created_by || (await User.me())?.email;
          const inc = Math.round(totalListenTime);
          let current = progress;

          if (!current) {
            const list = await UserProgress.filter({ created_by, lesson_id: lessonId });
            current = (Array.isArray(list) && list[0]) || null;
          }
          if (current?.id) {
            await UserProgress.update(current.id, {
              lesson_view_count: (current.lesson_view_count || 0) + 1,
              lesson_seconds_watched: (current.lesson_seconds_watched || 0) + inc,
              lesson_last_view_at: new Date().toISOString(),
            });
          } else {
            await UserProgress.create({
              created_by,
              lesson_id: lessonId,
              lesson_completed: true,
              exercise_completed: false,
              lesson_view_count: 1,
              lesson_seconds_watched: inc,
              lesson_last_view_at: new Date().toISOString(),
            });
          }
        } catch (e) {
          console.warn("UserProgress lesson update failed:", e);
        }
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, progress, totalListenTime]);

  // ---- Esercizio & Diario ----
  const handleExerciseComplete = async (answer) => {
    try {
      const patch = {
        lesson_id: lessonId,
        lesson_completed: true,
        exercise_completed: true,
        exercise_answer: answer,
        completion_date: new Date().toISOString(),
      };

      let updated;
      if (progress?.id) updated = await UserProgress.update(progress.id, patch);
      else updated = await UserProgress.create(patch);
      setProgress(updated);

      await syncExerciseWithDiary(answer);

      await saveLessonEvent({
        lesson,
        event: { type: "mark_completed", exercise_answer_present: !!answer }
      });

      setShowExercise(false);
      setShowFeedback(true);
    } catch (error) {
      console.error("Errore invio esercizio:", error);
    }
  };

  const syncExerciseWithDiary = async (answer) => {
    if (!lesson || !lesson.order) return;

    const diaryUpdate = {};
    if ([5, 7, 9, 10].includes(lesson.order)) {
      if (lesson.order === 5) diaryUpdate.esercizio_5_completato = true;
      if (lesson.order === 7) diaryUpdate.esercizio_7_completato = true;
      if (lesson.order === 9) diaryUpdate.esercizio_9_completato = true;
      if (lesson.order === 10) diaryUpdate.abitudini_potenzianti_completate = true;
    } else if (lesson.order === 6 && answer) {
      diaryUpdate.ricompense_obiettivi = answer;
    } else {
      return;
    }

    try {
      const today = new Date().toISOString().split("T")[0];
      const currentUser = await User.me();
      const checkIns = await DailyCheckIn.filter({ created_by: currentUser.email, date: today });
      if (checkIns.length > 0) await DailyCheckIn.update(checkIns[0].id, diaryUpdate);
    } catch (error) {
      console.error("Errore sincronizzazione diario:", error);
    }
  };

  const handleFeedbackSubmit = async () => {
    try {
      // opzionale: salva entity locale
    } catch (e) {
      console.warn("Feedback entity (locale) non salvata:", e);
    } finally {
      setShowFeedback(false);
      navigate(createPageUrl("Lessons"));
    }
  };

  // =======================
  // PATCH: rilevamento esercizio
  // =======================
  const hasExercise = (l) => {
    const x = l || {};
    const typed = String(x.exercise_type || "").toLowerCase().trim();
    const hasGoals =
      typed === "goals_rewards" ||
      !!x.goals_rewards_json ||
      !!x.goals_rewards ||
      (Array.isArray(x.obiettivi_ricompense) && x.obiettivi_ricompense.length > 0);

    return Boolean(
      x.exercise_instructions ||
      x.exercise_question ||
      x.exercise ||
      x.exercise_text ||
      x.exercise_html ||
      x.exercise_steps ||
      x.has_exercise ||
      hasGoals ||
      Number(x.order) === 6 // fallback robusto per la #6
    );
  };

  const isIntroLesson = ["-1", "0"].includes(String(lesson?.order ?? ""));
  const lessonHasExercise = hasExercise(lesson);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <>
      <div className="min-h-screen p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Button
              variant="outline"
              onClick={() => navigate(createPageUrl("Lessons"))}
              className="mb-4 hover:scale-105 transition-transform"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alle Lezioni
            </Button>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{lesson.title}</h1>
            {lesson.description && <p className="text-lg text-gray-600">{lesson.description}</p>}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="mb-8 shadow-xl bg-gradient-to-r from-blue-50 to-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Play className="w-6 h-6 text-blue-600" />
                  Lezione Audio
                  {progress?.lesson_completed && <CheckCircle className="w-6 h-6 text-green-500 ml-auto" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {resolvedSrc ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <audio
                        ref={audioRef}
                        controls
                        className="w-full"
                        src={resolvedSrc}
                        onPlay={handleAudioPlay}
                        onPause={handleAudioPause}
                        onSeeked={handleAudioSeeked}
                        onTimeUpdate={handleAudioTimeUpdate}
                        onEnded={handleAudioEnded}
                        onError={handleAudioError}
                        onLoadStart={handleAudioLoadStart}
                        onLoadedData={handleAudioLoadedData}
                        onCanPlay={handleAudioCanPlay}
                        onLoadedMetadata={(e) => setDuration(e.target.duration)}
                        preload="metadata"
                      >
                        Il tuo browser non supporta la riproduzione audio.
                      </audio>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Apri sorgente attuale:</p>
                      <a
                        href={resolvedSrc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Apri in nuova scheda
                      </a>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{lesson.duration} minuti</span>
                      </div>
                      {hasRevisited && (
                        <div className="flex items-center gap-1 text-orange-600">
                          <RefreshCw className="w-4 h-4" />
                          <span>Rivisto</span>
                        </div>
                      )}
                      {progress?.lesson_completed && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Lezione completata</span>
                        </div>
                      )}
                      {totalListenTime > 0 && (
                        <div className="text-blue-600">Tempo ascolto: {Math.floor(totalListenTime)}s</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">Nessun file audio caricato per questa lezione</p>
                    <Button
                      onClick={async () => {
                        await handleAudioPlay({ target: { currentTime: 0, paused: true } });
                        await sendEvent("complete");
                      }}
                      className="mt-4 bg-gradient-to-r from-blue-500 to-purple-600"
                    >
                      Segna come Ascoltato
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* ====== PATCH UI: mostra “nessun esercizio” SOLO per lezioni -1 e 0 ====== */}
          {isIntroLesson ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
              <Card className="shadow-xl text-center">
                <CardHeader>
                  <CardTitle className="text-2xl">Nessun esercizio per questa lezione</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-gray-700">
                    Dopo averla finita, prosegui pure con la lezione successiva.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <>
              {progress?.lesson_completed && !progress?.exercise_completed && lessonHasExercise && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                  <Card className="shadow-xl text-center">
                    <CardHeader>
                      <CardTitle className="text-2xl">Pronto per l'esercizio?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-lg text-gray-700 mb-6">
                        È il momento di mettere in pratica ciò che hai imparato.
                      </p>
                      <Button
                        onClick={openExerciseSafe}
                        size="lg"
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                      >
                        Inizia Esercizio
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {progress?.exercise_completed && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center p-4 bg-green-50 rounded-xl space-y-3"
                >
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                    <p className="text-green-700 font-semibold text-lg">Ottimo lavoro! Esercizio completato!</p>
                  </div>
                  <Button onClick={openExerciseSafe} size="sm" variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Ripeti Esercizio
                  </Button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showExercise && (
          (String(lesson?.order) === "6" || String(lesson?.exercise_type || "").toLowerCase() === "goals_rewards") ? (
            <ExerciseGoals lesson={lesson} onClose={() => setShowExercise(false)} />
          ) : (
            <ExercisePlayer
              lesson={lesson}
              previousAnswer={progress?.exercise_answer}
              onClose={() => setShowExercise(false)}
              onSubmit={handleExerciseComplete}
            />
          )
        )}
        {showFeedback && (
          <LessonFeedbackModal
            onSubmit={handleFeedbackSubmit}
            onClose={() => navigate(createPageUrl("Lessons"))}
          />
        )}
      </AnimatePresence>
    </>
  );
}
